import { createContext, KeyboardEvent, MouseEvent, ReactNode, useState } from 'react'

import { useGridNavigation } from '../hooks/useGridNavigation'
import { Cell, Id } from '../types'
import { VirtualCell, VirtualGrid } from './virtualGrid'

export type { ArrowKey } from '../hooks/useGridNavigation'
export { arrowKeys, isArrowKey } from '../hooks/useGridNavigation'

export type SelectionMode = 'none' | 'single' | 'multiple'

export type Cursor = { rowId: Id; colId: Id } | undefined

export type DataGridContextType = {
    cursor: Cursor
    selectedCells: Cell[]
    virtualSelectedCells: VirtualCell[]
    setDefault: () => void
    onMouseUp: (event: MouseEvent, rowId: Id, colId: Id) => void
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
    getVirtualCell: VirtualGrid['getVirtualCell']
    setSelection: (cells: Cell[]) => void
}

export const DataGridContext = createContext<DataGridContextType | undefined>(undefined)

type DataGridProviderProps<TRow extends { id: Id }, TCol extends { id: Id }> = {
    rows: TRow[]
    cols: TCol[]
    selectionMode?: SelectionMode
    allowDeselect?: boolean
    children: ReactNode
    selectedCells?: Cell[]
    onSelectionChange?: (cells: Cell[]) => void
}

export function DataGridProvider<TRow extends { id: Id }, TCol extends { id: Id }>({ children, rows, cols, ...options }: DataGridProviderProps<TRow, TCol>) {
    const [ids, setIds] = useState(() => ({
        rowIds: rows.map(row => row.id),
        colIds: cols.map(col => col.id),
    }))

    const sameRows = rows.length === ids.rowIds.length && rows.every((row, index) => row.id === ids.rowIds[index])
    const sameCols = cols.length === ids.colIds.length && cols.every((col, index) => col.id === ids.colIds[index])

    if (!sameRows || !sameCols) {
        setIds({
            rowIds: rows.map(row => row.id),
            colIds: cols.map(col => col.id),
        })
    }

    const value = useGridNavigation({ ...options, ...ids })
    return <DataGridContext value={value}>{children}</DataGridContext>
}
